<script lang="ts">
	import Pre from '#lib/html/input/Pre.svelte';
	import type { DiagnosticCategory, DiagnosticSeverity } from '#lib/stores/diagnostics.js';
	import { t } from '#lib/stores/i18n.js';
	import { AlertCircle, AlertTriangle, ChevronDown, Info } from '@lucide/svelte';
	import { slide } from 'svelte/transition';

	export interface GroupedDiagnosticEntry {
		id: string;
		severity: DiagnosticSeverity;
		category: DiagnosticCategory;
		title: string;
		message: string;
		details?: Record<string, unknown> | string | null;
		url?: string;
		stack?: string;
		isThirdParty?: boolean;
		count: number;
		latestTimestamp: string;
		firstTimestamp: string;
	}

	interface Props {
		entry: GroupedDiagnosticEntry;
		isExpanded: boolean;
		ontoggle: () => void;
	}

	let { entry, isExpanded, ontoggle }: Props = $props();

	function shouldShowSecondaryMessage(title?: string, message?: string): boolean {
		if (!message || !title) return false;
		const cleanTitle = title.trim().toLowerCase();
		const cleanMessage = message.trim().toLowerCase();
		if (cleanTitle === cleanMessage) return false;
		if (cleanMessage.replace(/\.+$/, '') === cleanTitle.replace(/\.+$/, '')) return false;
		return true;
	}

	function getCategoryBadgeClasses(category: DiagnosticCategory): string {
		switch (category) {
			case 'api':
				return 'bg-purple-500/10 text-purple-600 dark:text-purple-300 border border-purple-500/15';
			case 'runtime':
				return 'bg-surface-on/10 dark:bg-white/10 text-surface-on/70 dark:text-slate-300 border border-border/40 dark:border-white/10';
			case 'websocket':
				return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border border-emerald-500/15';
			default:
				return 'bg-slate-500/10 text-slate-600 dark:text-slate-300 border border-slate-500/15';
		}
	}

	function getCardAccentClasses(severity: DiagnosticSeverity): {
		borderLeft: string;
		iconBg: string;
		iconText: string;
	} {
		switch (severity) {
			case 'error':
				return {
					borderLeft: 'border-l-red-500 dark:border-l-red-400',
					iconBg: 'bg-red-500/10 dark:bg-red-500/20',
					iconText: 'text-red-600 dark:text-red-400'
				};
			case 'warning':
				return {
					borderLeft: 'border-l-amber-500 dark:border-l-amber-400',
					iconBg: 'bg-amber-500/10 dark:bg-amber-500/20',
					iconText: 'text-amber-600 dark:text-amber-400'
				};
			case 'info':
			default:
				return {
					borderLeft: 'border-l-blue-500 dark:border-l-blue-400',
					iconBg: 'bg-blue-500/10 dark:bg-blue-500/20',
					iconText: 'text-blue-600 dark:text-blue-400'
				};
		}
	}

	function formatTimestamp(isoString: string): string {
		if (!isoString) return '';
		return isoString.includes('T') ? isoString.split('T')[1]?.slice(0, 8) || '' : isoString;
	}

	let accent = $derived(getCardAccentClasses(entry.severity));
	let hasSecondary = $derived(shouldShowSecondaryMessage(entry.title, entry.message));
</script>

<div
	id="diagnostic-incident-{entry.id}"
	class="rounded-xl border border-border/60 dark:border-white/10 border-l-[3.5px] {accent.borderLeft} bg-surface-on/[0.02] dark:bg-white/[0.02] transition-all text-xs flex flex-col overflow-hidden"
>
	<button
		type="button"
		class="w-full p-3 flex items-start justify-between gap-3 cursor-pointer select-none text-left focus:outline-none hover:bg-surface-on/[0.05] dark:hover:bg-white/[0.05] transition-colors"
		onclick={ontoggle}
		aria-expanded={isExpanded}
	>
		<div class="flex items-start gap-2.5 min-w-0 flex-1">
			<!-- Severity Icon -->
			<div
				class="w-6 h-6 rounded-lg {accent.iconBg} {accent.iconText} flex items-center justify-center shrink-0 mt-0.5"
			>
				{#if entry.severity === 'error'}
					<AlertCircle class="w-3.5 h-3.5" />
				{:else if entry.severity === 'warning'}
					<AlertTriangle class="w-3.5 h-3.5" />
				{:else}
					<Info class="w-3.5 h-3.5" />
				{/if}
			</div>

			<div class="min-w-0 flex-1">
				<!-- Tags + Title + Multiplier -->
				<div class="flex items-center gap-1.5 flex-wrap">
					<span
						class="text-3xs uppercase tracking-wider font-mono font-semibold px-1.5 py-0.5 rounded {getCategoryBadgeClasses(
							entry.category
						)}"
					>
						{entry.category}
					</span>

					{#if entry.isThirdParty}
						<span
							class="text-3xs uppercase tracking-wider font-mono font-semibold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
						>
							{$t('diagnostics.third_party')}
						</span>
					{/if}

					{#if entry.count > 1}
						<span
							class="text-2xs font-bold font-mono px-1.5 py-0.2 rounded-md bg-surface-on/10 dark:bg-white/10 text-surface-on dark:text-white border border-border/50 dark:border-white/10"
							title={$t('diagnostics.repeated_times', { count: entry.count })}
						>
							×{entry.count}
						</span>
					{/if}

					<span class="font-semibold text-surface-on dark:text-white truncate">
						{entry.title}
					</span>
				</div>

				<!-- Secondary distinct message if non-redundant -->
				{#if hasSecondary}
					<p
						class="text-surface-on/60 dark:text-slate-400 text-2xs truncate font-mono mt-0.5 pl-0.5 leading-relaxed"
					>
						{entry.message}
					</p>
				{/if}
			</div>
		</div>

		<!-- Timestamp & Chevron -->
		<div
			class="flex items-center gap-2 shrink-0 text-surface-on/50 dark:text-slate-400 text-2xs font-mono pt-0.5"
		>
			<span>{formatTimestamp(entry.latestTimestamp)}</span>
			<ChevronDown
				class="w-4 h-4 transition-transform duration-200 {isExpanded ? 'rotate-180' : ''}"
			/>
		</div>
	</button>

	<!-- Expanded Area -->
	{#if isExpanded}
		<div
			transition:slide={{ duration: 200 }}
			class="px-3 pb-3 pt-3 border-t border-border/40 dark:border-white/10 space-y-2.5 text-xs"
		>
			{#if entry.count > 1}
				<div
					class="text-2xs text-surface-on/60 dark:text-slate-400 font-mono px-2.5 py-1 rounded-lg bg-surface-on/5 dark:bg-white/5 border border-border/30 dark:border-white/5 flex items-center gap-2"
				>
					<span class="font-semibold"
						>{$t('diagnostics.repeated_times', { count: entry.count })}</span
					>
					{#if entry.firstTimestamp !== entry.latestTimestamp}
						<span class="text-surface-on/40 dark:text-slate-500">
							({formatTimestamp(entry.firstTimestamp)} ➔ {formatTimestamp(
								entry.latestTimestamp
							)})
						</span>
					{/if}
				</div>
			{/if}

			{#if entry.url}
				<div
					class="flex items-center gap-2 p-2 rounded-xl bg-surface-on/5 dark:bg-white/5 border border-border/40 dark:border-white/5"
				>
					<span
						class="text-surface-on/50 dark:text-slate-400 font-semibold text-2xs uppercase tracking-wider shrink-0"
					>
						{$t('diagnostics.url')}:
					</span>
					<span
						class="font-mono text-2xs break-all select-all text-surface-on dark:text-slate-200"
					>
						{entry.url}
					</span>
				</div>
			{/if}

			{#if entry.details}
				<div>
					<span
						class="text-surface-on/60 dark:text-slate-400 font-semibold text-2xs uppercase tracking-wider"
					>
						{$t('diagnostics.details')}:
					</span>
					<div
						class="mt-1 rounded-xl bg-black text-emerald-400 p-3 overflow-x-auto border border-white/15 font-mono text-2xs shadow-inner"
					>
						<Pre>{JSON.stringify(entry.details, null, 2)}</Pre>
					</div>
				</div>
			{/if}

			{#if entry.stack}
				<div>
					<span
						class="text-surface-on/60 dark:text-slate-400 font-semibold text-2xs uppercase tracking-wider"
					>
						{$t('diagnostics.stack')}:
					</span>
					<div
						class="mt-1 rounded-xl bg-[#140606] text-red-300 dark:text-red-200/90 p-3 overflow-x-auto border border-red-500/30 font-mono text-2xs whitespace-pre select-all shadow-inner max-h-60 overflow-y-auto"
					>
						<pre class="leading-relaxed">{entry.stack}</pre>
					</div>
				</div>
			{/if}
		</div>
	{/if}
</div>
